import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import { formatVersionWithCode } from '../utils/versionLabel'
import {
  cloneEmptyPolicyForm,
  mapPolicyToForm,
  validatePolicyForm,
  buildPolicyPayload,
} from './public-api-access/publicApiLimitPolicyForm'
import { POLICY_STATUS_OPTIONS, formatSubjectDisplay } from './public-api-access/publicApiAccessPageUtils'
import { listMobileDevices, getMobileDevice, updateMobileDeviceStatus, updateMobileDeviceLimits } from '../services/mobileDeviceAdminService'

const initialFilters = {
  platform: '',
  status: '',
  appVersion: '',
  userId: '',
  hasPushToken: '',
  lastSeenFrom: '',
  lastSeenTo: '',
  search: '',
}

function getStatusPillClass(status) {
  const normalized = String(status || '').trim().toLowerCase()
  if (normalized === 'active') return ui.badges.success
  if (normalized === 'blocked' || normalized === 'archived' || normalized === 'disabled') return ui.badges.danger
  return ui.badges.neutral
}

export default function MobileDevicesAdmin() {
  const timezoneColumnClass = 'w-44 max-w-44 truncate'
  const [filters, setFilters] = useState(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)
  const [sortConfig, setSortConfig] = useState({ key: 'lastSeenAt', direction: 'desc' })
  const [refreshTick, setRefreshTick] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [limitForm, setLimitForm] = useState(cloneEmptyPolicyForm())
  const [limitErrors, setLimitErrors] = useState({})
  const [pendingStatus, setPendingStatus] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    listMobileDevices({
      ...appliedFilters,
      sortBy: sortConfig.key,
      sortDirection: sortConfig.direction,
    })
      .then((data) => {
        if (cancelled) return
        setItems(Array.isArray(data) ? data : [])
      })
      .catch((requestError) => {
        if (cancelled) return
        setError(requestError?.message || 'Unable to load mobile devices')
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appliedFilters, refreshTick, sortConfig])

  useEffect(() => {
    if (!selectedId) {
      setSelectedDevice(null)
      setDetailError('')
      setLimitForm(cloneEmptyPolicyForm())
      return
    }

    let cancelled = false
    setDetailLoading(true)
    setDetailError('')
    getMobileDevice(selectedId)
      .then((data) => {
        if (cancelled) return
        setSelectedDevice(data)
        setLimitForm(mapPolicyToForm(data.effectivePolicy || {}))
      })
      .catch((requestError) => {
        if (cancelled) return
        setDetailError(requestError?.message || 'Unable to load device details')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, refreshTick])

  const summary = useMemo(() => ({
    total: items.length,
    active: items.filter((device) => device.status === 'active').length,
    blocked: items.filter((device) => device.status === 'blocked').length,
    archived: items.filter((device) => device.status === 'archived').length,
  }), [items])

  const selectedRow = selectedId ? items.find((device) => device.id === selectedId) || null : null
  const detail = selectedDevice || selectedRow
  const detailDevice = detail?.device || detail
  const detailLinkedUser = detail?.linkedUser || detailRowUser(selectedRow)
  const effectivePolicy = detail?.effectivePolicy || selectedRow?.effectivePolicy || null
  const latestPublishedRelease = detail?.latestPublishedRelease || selectedRow?.latestPublishedRelease || null
  const latestPublishedReleaseLabel = formatVersionWithCode(
    latestPublishedRelease?.versionName,
    latestPublishedRelease?.versionCode,
  )

  function applyFilters() {
    setAppliedFilters({ ...filters })
  }

  function resetFilters() {
    setFilters(initialFilters)
    setAppliedFilters(initialFilters)
  }

  function openDevice(device) {
    setSelectedId(device.id)
    setSelectedDevice(device)
    setLimitForm(mapPolicyToForm(device.effectivePolicy || {}))
  }

  function detailRowUser(row) {
    return row?.user || null
  }

  function formatLastSeenParts(lastSeenAt) {
    if (!lastSeenAt) return { datePart: null, timePart: null }
    const formatted = formatDateTime(lastSeenAt)
    const [datePart, timePart] = formatted.split(' ')
    return {
      datePart: datePart || formatted,
      timePart: timePart || null,
    }
  }

  async function saveLimits(event) {
    event.preventDefault()
    if (!selectedId) return
    const errors = validatePolicyForm(limitForm)
    delete errors.subjectType
    delete errors.subjectId
    delete errors.endpointGroup
    setLimitErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      await updateMobileDeviceLimits(selectedId, buildPolicyPayload(limitForm))
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to update mobile device limits')
    } finally {
      setSaving(false)
    }
  }

  async function updateStatus() {
    if (!pendingStatus) return
    setSaving(true)
    try {
      await updateMobileDeviceStatus(pendingStatus.device.id, pendingStatus.status)
      setPendingStatus(null)
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to update mobile device status')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState text="Loading mobile devices..." />
      </PageShell>
    )
  }

  if (error && items.length === 0) {
    return (
      <PageShell>
        <ErrorState text={error} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
      </PageShell>
    )
  }

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Mobile Devices"
        subtitle="Inspect registered Flutter installs, their effective public API limits, and current device status."
        actions={<Button variant="secondary" onClick={() => setRefreshTick((value) => value + 1)}>Refresh</Button>}
      />

      {error && items.length > 0 ? <ErrorState text={error} /> : null}

      <SectionCard title="Filters" subtitle="Filter by platform, user, app version, push token presence, and last seen range.">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input placeholder="Platform" value={filters.platform} onChange={(event) => setFilters((current) => ({ ...current, platform: event.target.value }))} />
          <Input placeholder="Status" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} />
          <Input placeholder="App version" value={filters.appVersion} onChange={(event) => setFilters((current) => ({ ...current, appVersion: event.target.value }))} />
          <Input placeholder="Linked user id" value={filters.userId} onChange={(event) => setFilters((current) => ({ ...current, userId: event.target.value }))} />
          <select className={ui.select} value={filters.hasPushToken} onChange={(event) => setFilters((current) => ({ ...current, hasPushToken: event.target.value }))}>
            <option value="">Push token: all</option>
            <option value="true">Has push token</option>
            <option value="false">No push token</option>
          </select>
          <Input placeholder="Search" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          <Input type="date" value={filters.lastSeenFrom} onChange={(event) => setFilters((current) => ({ ...current, lastSeenFrom: event.target.value }))} />
          <Input type="date" value={filters.lastSeenTo} onChange={(event) => setFilters((current) => ({ ...current, lastSeenTo: event.target.value }))} />
          <div className="flex items-end gap-2 xl:col-span-4">
            <Button onClick={applyFilters}>Apply filters</Button>
            <Button variant="secondary" onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Summary">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['Total', summary.total],
            ['Active', summary.active],
            ['Blocked', summary.blocked],
            ['Archived', summary.archived],
          ].map(([label, value]) => (
            <div key={label} className="rounded border border-outline bg-surface-elevated p-4">
              <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
              <div className="mt-1 text-2xl font-semibold">{value}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Devices">
        {items.length === 0 ? (
          <EmptyState title="No mobile devices found" description="Try clearing filters or broadening the last seen range." />
        ) : (
          <DataTable
            sortState={sortConfig}
            onSortChange={setSortConfig}
            disableClientSort
            tableClassName="table-fixed"
            columns={[
              { key: 'deviceIdShort', label: 'Device', sortable: true, disableSortClear: true, sortAccessor: (device) => device.deviceIdShort || device.deviceIdHash || device.id },
              { key: 'user', label: 'User', sortable: true, disableSortClear: true, sortAccessor: (device) => device.user?.email || device.user?.name || device.user?.id || '' },
              { key: 'platform', label: 'Platform', sortable: true, disableSortClear: true, sortAccessor: (device) => device.platform || '' },
              { key: 'appVersion', label: 'ver', sortable: true, disableSortClear: true, sortAccessor: (device) => device.appVersion || '' },
              { key: 'locale', label: 'Locale', sortable: true, disableSortClear: true, sortAccessor: (device) => device.locale || '' },
              { key: 'timezone', label: 'Timezone', sortable: true, disableSortClear: true, sortAccessor: (device) => device.timezone || '', className: timezoneColumnClass },
              { key: 'pushProvider', label: 'Push', sortable: true, disableSortClear: true, sortAccessor: (device) => (device.hasPushToken ? device.pushProvider || '' : 'none') },
              { key: 'status', label: 'Status', sortable: true, disableSortClear: true, sortAccessor: (device) => device.status || '' },
              { key: 'lastSeenAt', label: 'Last seen', sortable: true, disableSortClear: true, sortAccessor: (device) => device.lastSeenAt || '' },
              { key: 'effectiveLimit', label: 'limit', sortable: true, disableSortClear: true, sortAccessor: (device) => device.effectiveLimit?.maxRequests ?? null },
              { key: 'actions', label: 'Actions' },
            ]}
            rows={items}
            renderRow={(device) => (
              <tr key={device.id}>
                <td className={ui.tableCellText}>{device.deviceIdShort}</td>
                <td className={ui.tableCellText}>{device.user?.email || device.user?.name || formatEmptyValue(device.user?.id)}</td>
                <td className={ui.tableCellText}>{device.platform}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(device.appVersion)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(device.locale)}</td>
                <td className={`${ui.tableCellText} ${timezoneColumnClass}`.trim()} title={formatEmptyValue(device.timezone)}>
                  {formatEmptyValue(device.timezone)}
                </td>
                <td className={ui.tableCellText}>{device.hasPushToken ? device.pushProvider : 'none'}</td>
                <td className={ui.tableCell}><span className={getStatusPillClass(device.status)}>{device.status}</span></td>
                <td className={ui.tableCellText}>
                  {device.lastSeenAt ? (
                    (() => {
                      const { datePart, timePart } = formatLastSeenParts(device.lastSeenAt)
                      return (
                        <div className="flex flex-col leading-tight">
                          <span>{datePart}</span>
                          {timePart ? <span className="text-xs text-muted-foreground">{timePart}</span> : null}
                        </div>
                      )
                    })()
                  ) : (
                    formatEmptyValue(null)
                  )}
                </td>
                <td className={ui.tableCellText}>{formatEmptyValue(device.effectiveLimit?.maxRequests ?? null)}</td>
                <td className={ui.tableCell}>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => openDevice(device)}>View details</Button>
                    <Button size="sm" variant="secondary" onClick={() => { setSelectedId(device.id); setSelectedDevice(null) }}>Set limits</Button>
                    {device.status !== 'blocked' ? <Button size="sm" variant="danger" onClick={() => setPendingStatus({ device, status: 'blocked' })}>Block</Button> : <Button size="sm" variant="secondary" onClick={() => setPendingStatus({ device, status: 'active' })}>Unblock</Button>}
                    {device.status !== 'archived' ? <Button size="sm" variant="danger" onClick={() => setPendingStatus({ device, status: 'archived' })}>Archive</Button> : null}
                  </div>
                </td>
              </tr>
            )}
          />
        )}
      </SectionCard>

      <SectionCard title="Selected device" subtitle={detail ? `${detailDevice.deviceIdShort || detailDevice.id || '-'} ${formatSubjectDisplay('mobile_device', detailDevice.id)}` : 'Choose a device to inspect its metadata, limits, and usage.'}>
        {!selectedId ? (
          <EmptyState title="No device selected" description="Click View details to inspect a device." />
        ) : detailLoading ? (
          <LoadingState text="Loading device details..." />
        ) : detailError ? (
          <ErrorState text={detailError} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
        ) : detail ? (
          <div className="grid gap-4 xl:grid-cols-[1.3fr_0.9fr]">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Device id hash</div><div className="mt-1 text-sm break-all">{detailDevice.deviceIdHash}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Linked user</div><div className="mt-1 text-sm">{detailLinkedUser?.email || detailLinkedUser?.name || formatEmptyValue(detailLinkedUser?.id)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">App version</div><div className="mt-1 text-sm">{formatEmptyValue(detailDevice.appVersion)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Last seen</div><div className="mt-1 text-sm">{formatEmptyValue(detailDevice.lastSeenAt ? formatDateTime(detailDevice.lastSeenAt) : null)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Update status</div><div className="mt-1 text-sm">{latestPublishedRelease ? `${latestPublishedReleaseLabel} (${latestPublishedRelease.status})` : 'No published release'}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Notifications</div><div className="mt-1 text-sm">{detail?.notificationStats?.total || 0} total, {detail?.notificationStats?.sent || 0} sent</div></div>
              </div>
              <SectionCard title="Recent usage" className="p-0">
                {Array.isArray(detail?.usageBuckets) && detail.usageBuckets.length > 0 ? (
                  <DataTable
                    columns={[
                      { key: 'bucketType', label: 'Bucket', sortable: true },
                      { key: 'bucketStart', label: 'Start', sortable: true, sortAccessor: (bucket) => bucket.bucketStart || '' },
                      { key: 'requestCount', label: 'Requests', sortable: true, sortAccessor: (bucket) => Number(bucket.requestCount || 0) },
                      { key: 'acceptedCount', label: 'Accepted', sortable: true, sortAccessor: (bucket) => Number(bucket.acceptedCount || 0) },
                      { key: 'rejectedCount', label: 'Rejected', sortable: true, sortAccessor: (bucket) => Number(bucket.rejectedCount || 0) },
                      { key: 'costUnits', label: 'Cost', sortable: true, sortAccessor: (bucket) => Number(bucket.costUnits || 0) },
                    ]}
                    rows={detail.usageBuckets}
                    renderRow={(bucket) => (
                      <tr key={bucket.id}>
                        <td className={ui.tableCellText}>{bucket.bucketType}</td>
                        <td className={ui.tableCellText}>{formatEmptyValue(bucket.bucketStart ? formatDateTime(bucket.bucketStart) : null)}</td>
                        <td className={ui.tableCellText}>{bucket.requestCount}</td>
                        <td className={ui.tableCellText}>{bucket.acceptedCount}</td>
                        <td className={ui.tableCellText}>{bucket.rejectedCount}</td>
                        <td className={ui.tableCellText}>{bucket.costUnits}</td>
                      </tr>
                    )}
                  />
                ) : (
                  <EmptyState title="No usage buckets" description="This device has no usage history in the selected window." />
                )}
              </SectionCard>
            </div>

            <SectionCard title="Effective limit">
              <form className="grid gap-3" onSubmit={saveLimits}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Window seconds" error={limitErrors.windowSeconds}><Input type="number" min="60" value={limitForm.windowSeconds} onChange={(event) => setLimitForm((current) => ({ ...current, windowSeconds: event.target.value }))} /></FormField>
                  <FormField label="Max requests" error={limitErrors.maxRequests}><Input type="number" min="1" value={limitForm.maxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, maxRequests: event.target.value }))} /></FormField>
                  <FormField label="Cost units" error={limitErrors.maxCostUnits}><Input type="number" min="0" value={limitForm.maxCostUnits} onChange={(event) => setLimitForm((current) => ({ ...current, maxCostUnits: event.target.value }))} /></FormField>
                  <FormField label="Concurrent jobs" error={limitErrors.maxConcurrentJobs}><Input type="number" min="0" value={limitForm.maxConcurrentJobs} onChange={(event) => setLimitForm((current) => ({ ...current, maxConcurrentJobs: event.target.value }))} /></FormField>
                  <FormField label="Daily max" error={limitErrors.dailyMaxRequests}><Input type="number" min="0" value={limitForm.dailyMaxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, dailyMaxRequests: event.target.value }))} /></FormField>
                  <FormField label="Monthly max" error={limitErrors.monthlyMaxRequests}><Input type="number" min="0" value={limitForm.monthlyMaxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, monthlyMaxRequests: event.target.value }))} /></FormField>
                  <FormField label="Priority" error={limitErrors.priority}><Input type="number" min="0" value={limitForm.priority} onChange={(event) => setLimitForm((current) => ({ ...current, priority: event.target.value }))} /></FormField>
                  <FormField label="Status" error={limitErrors.status}>
                    <select className={ui.select} value={limitForm.status} onChange={(event) => setLimitForm((current) => ({ ...current, status: event.target.value }))}>
                      {POLICY_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </FormField>
                </div>
                <FormField label="Reason"><Input as="textarea" rows={3} value={limitForm.reason} onChange={(event) => setLimitForm((current) => ({ ...current, reason: event.target.value }))} /></FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Effective from" error={limitErrors.effectiveFrom}><Input type="date" value={limitForm.effectiveFrom} onChange={(event) => setLimitForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></FormField>
                  <FormField label="Effective to" error={limitErrors.effectiveTo}><Input type="date" value={limitForm.effectiveTo} onChange={(event) => setLimitForm((current) => ({ ...current, effectiveTo: event.target.value }))} /></FormField>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={saving}>Save limits</Button>
                  <Button type="button" variant="secondary" onClick={() => setLimitForm(mapPolicyToForm(effectivePolicy || {}))}>Reset</Button>
                </div>
              </form>
            </SectionCard>
          </div>
        ) : null}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingStatus)}
        title="Change device status"
        message={pendingStatus ? `Set ${pendingStatus.device.deviceIdShort} to ${pendingStatus.status}?` : ''}
        confirmText="Apply"
        confirmVariant={pendingStatus?.status === 'blocked' ? 'danger' : 'primary'}
        loading={saving}
        onConfirm={updateStatus}
        onCancel={() => setPendingStatus(null)}
      />
    </PageShell>
  )
}
